import Auth from '@aws-amplify/auth';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import mimeTypes from 'mime-types';
import { MODULE_INFO } from '@/config/constants';
import moment from 'moment';
moment.updateLocale(moment.locale(), { invalidDate: '—' });

const API_BASE = process.env.API_BASE;
const HS_API_BASE = process.env.HS_API_BASE;
const MP_API_BASE = process.env.MP_API_BASE;
const LX_API_BASE = process.env.LX_API_BASE;
const TD_API_BASE = process.env.TD_API_BASE;
const DO_API_BASE = process.env.DO_API_BASE;
const LB_API_BASE = process.env.LB_API_BASE;
const UM_API_BASE = process.env.UM_API_BASE;
const LCC_API_BASE = process.env.LCC_API_BASE;
const MR_API_BASE = process.env.MR_API_BASE;
const RAIL_API_BASE = process.env.RAIL_API_BASE;
const EL_API_BASE = process.env.EL_API_BASE;
const CD_API_BASE = process.env.CD_API_BASE;
const CS_API_BASE = process.env.CS_API_BASE;
const CDS_API_BASE = process.env.CDS_API_BASE;
const PD_API_BASE = process.env.PD_API_BASE;
const AM_API_BASE = process.env.AM_API_BASE;
const MB_API_BASE = process.env.MB_API_BASE;
const CLP_API_BASE = process.env.CLP_API_BASE;
const CLP_GOV_API_BASE = process.env.CLP_GOV_API_BASE;
const IN_API_BASE = process.env.IN_API_BASE;
const ES_API_BASE = process.env.ES_API_BASE;
const BASE_URL = process.env.BASE_URL;
const environment = process.env.ENV_NAME;

function getCookie(cookie) {
    let matches = document.cookie.match(new RegExp(`(^| )${cookie}=([^;]+)`));
    if (matches && matches.length === 3) {
        return matches[2];
    }
    return null;
}

// Retrieved from internet, hence the minified format.
export const formatBytes = (a, b) => {
    if (0 == a) return '0 Bytes';
    var c = 1024,
        d = b || 2,
        e = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        f = Math.floor(Math.log(a) / Math.log(c));
    return parseFloat((a / Math.pow(c, f)).toFixed(d)) + ' ' + e[f];
};

export const getFullUTC = dateString => {
    return moment.utc(dateString).format('DD-MMM-YYYY HH:MM:SS');
};
export const getRawUTC = dateString => {
    return moment.utc(dateString).format('DD-MMM-YYYY');
};
export const getSortableUTC = dateString => {
    return moment.utc(dateString);
};

function getLocalStorage(key) {
    return localStorage.getItem(key);
}
function setLocalStorage(key, value) {
    return localStorage.setItem(key, value);
}

// Allow other modules to use this function to redirect back after a login
function forceLogin(path) {
    document.cookie = `dc_redirect=${location.origin + path};path=/`;
    location.href = '/login';
    throw new Error('Not Authenticated');
}

/**
 * Software download for Sentient Artifacts/applications
 * Params:
 * filename
 * custerID
 */
const requestArtifactorySignedURL = async (customerID, filename) => {
    try {
        let requestPreSignedURL = await dataResource.umAPI.get(
            `/customers/${customerID}/users/download-artifact/${filename}`
        );
        return requestPreSignedURL;
    } catch (error) {
        return error;
    }
};

/**
 * Check FlexCM Access for specific customer
 * Name: checkCustomerFlexCMAccess
 * Parameters: (customerId)
 * Returns:
 * A boolean: true || false
 * Name: checkCustomerFlexCMAccess
 */
const getFlexCMAccessStatus = async customerId => {
    try {
        const request = await dataResource.umAPI.get(
            `/customers/flexcmaccess/${customerId}`
        );
        return request.data;
    } catch (error) {
        return error;
    }
};

/**
 * Function to download files requiring authorization, from API
 *
 * @param {string} apiType Key to the API in the dataResource object you wish to download from
 * @param {string} url Relative API URL to hit
 * @param {string} name Filename you want the file to save as, without the extension
 */
function downloadFile(apiType, url, name = 'download') {
    dataResource[apiType]
        .get(url, {
            responseType: 'blob'
        })
        .then(response => {
            let extension = mimeTypes.extension(response.data.type);
            let url = window.URL.createObjectURL(new Blob([response.data]));
            let link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${name}.${extension}`);
            document.body.appendChild(link);
            link.click();
        });
}

const makeAPI = baseURL => {
    let api = axios.create({ baseURL });
    api.interceptors.request.use(config =>
        Auth.currentSession().then(resp => {
            config.headers.Authorization = `Bearer ${resp.idToken.jwtToken}`;

            return Promise.resolve(config);
        })
    );
    return api;
};

let dataResource = {
    API: makeAPI(API_BASE),
    hsAPI: makeAPI(HS_API_BASE || API_BASE + '/asset-life'),
    mpAPI: makeAPI(MP_API_BASE),
    lxAPI: makeAPI(LX_API_BASE),
    tdAPI: makeAPI(TD_API_BASE),
    lccAPI: makeAPI(LCC_API_BASE),
    mrAPI: makeAPI(MR_API_BASE),
    railAPI: makeAPI(RAIL_API_BASE || API_BASE + '/rail'),
    cdAPI: makeAPI(CD_API_BASE),
    csAPI: makeAPI(CS_API_BASE),
    doAPI: DO_API_BASE ? makeAPI(DO_API_BASE) : undefined,
    lbAPI: LB_API_BASE ? makeAPI(LB_API_BASE) : undefined,
    umAPI: UM_API_BASE ? makeAPI(UM_API_BASE) : undefined,
    elAPI: EL_API_BASE ? makeAPI(EL_API_BASE) : undefined,
    pdAPI: PD_API_BASE ? makeAPI(PD_API_BASE) : undefined,
    amAPI: AM_API_BASE ? makeAPI(AM_API_BASE) : undefined,
    mbAPI: MB_API_BASE ? makeAPI(MB_API_BASE) : undefined,
    clpAPI: CLP_API_BASE ? makeAPI(CLP_API_BASE) : undefined,
    clpgovAPI: CLP_GOV_API_BASE ? makeAPI(CLP_GOV_API_BASE) : undefined,
    cdsAPI: CDS_API_BASE ? makeAPI(CDS_API_BASE) : undefined,
    inAPI: IN_API_BASE ? makeAPI(IN_API_BASE) : undefined,
    esAPI: ES_API_BASE ? makeAPI(ES_API_BASE) : undefined,
    baseURL: BASE_URL,
    apiURL: API_BASE,
    environment,
    getCookie,
    getLocalStorage,
    setLocalStorage,
    downloadFile,
    requestArtifactorySignedURL,
    getFlexCMAccessStatus,
    payload: () =>
        Auth.currentSession().then(resp => {
            let payload = jwtDecode(resp.idToken.jwtToken);
            payload.auth =
                payload['custom:auth'] && JSON.parse(payload['custom:auth']);
            payload.meta =
                payload['custom:meta'] && JSON.parse(payload['custom:meta']);

            let modules = [];
            if (payload.auth.modules) {
                modules = payload.auth.modules;
            } else if (payload.auth.role && payload.auth.role === 'sentient') {
                modules = Object.keys(MODULE_INFO);
            }
            payload.modules = modules;

            let landingPageModuleKey = null;
            if (payload.auth.landingPageModuleKey) {
                landingPageModuleKey = payload.auth.landingPageModuleKey;
            } else if (
                payload.modules &&
                Array.isArray(payload.modules) &&
                payload.modules.length > 0
            ) {
                landingPageModuleKey = payload.modules.filter(
                    module => Object.keys(MODULE_INFO).indexOf(module) != -1
                )[0];
            } else {
                console.error('Error setting the landing page in resources.js');
            }
            payload.landingPageModuleKey = landingPageModuleKey;

            return payload;
        }),
    getRawUTC: getRawUTC,
    getFullUTC: getFullUTC,
    getSortableUTC: getSortableUTC,
    formatBytes: formatBytes,
    setCustomerId: custId => {
        return Auth.currentSession().then(resp => {
            let payload = jwtDecode(resp.idToken.jwtToken);
            let authStr = payload['custom:auth'];
            if (!authStr) {
                return;
            }
            const auth = JSON.parse(authStr);
            if (auth.role === 'sentient') {
                localStorage.setItem('customerId', custId);
            }
        });
    },
    getCustomerId: async () => {
        const resp = await Auth.currentSession();
        let payload = jwtDecode(resp.idToken.jwtToken);
        let authStr = payload['custom:auth'];
        if (!authStr) {
            return;
        }
        const auth = JSON.parse(authStr);

        if (auth.role !== 'sentient') {
            return auth.customerId;
        }
        return localStorage.getItem('customerId');
    },
    setSiteGroups: (customerId, siteGroups) => {
        localStorage.setItem(
            `SiteGroups${customerId}`,
            JSON.stringify(siteGroups)
        );
    },
    getSiteGroups: customerId => {
        return JSON.parse(localStorage.getItem(`SiteGroups${customerId}`));
    },
    getSiteGroupsUrlParam: customerId => {
        const groupIds = JSON.parse(
            localStorage.getItem(`SiteGroups${customerId}`)
        );
        if (groupIds && groupIds.length > 0) {
            return `siteGroup=${groupIds.join(',')}`;
        }
        return '';
    },
    forceLogin
};

export default dataResource;



// WEBPACK FOOTER //
// ./src/util/resources.js